import React, { useEffect, useState } from 'react';
import { PortalShell } from '@/components/nav/PortalShell';
import { portalApi, uploadApi, uploadUrl } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Camera, X, TriangleAlert } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { PORTAL } from '@/constants/testIds';

export default function PortalReportsPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', severity: 'medium', property_id: '', photo_ids: [], photo_urls: [] });

  const load = () => portalApi.reports()
    .then(setItems)
    .catch(() => {}) // surfaced by the api response interceptor
    .finally(() => setLoaded(true));
  useEffect(() => { load(); portalApi.properties().then(setProperties).catch(() => {}); }, []);

  const attach = async (file) => {
    try {
      const up = await uploadApi.upload(file);
      setForm(f => ({ ...f, photo_ids: [...f.photo_ids, up.id], photo_urls: [...f.photo_urls, up.url] }));
    } catch { toast.error('Upload failed'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await portalApi.createReport({
        title: form.title, description: form.description, severity: form.severity,
        property_id: form.property_id || null, photo_ids: form.photo_ids,
      });
      toast.success('Report submitted — our team will follow up.');
      setOpen(false);
      setForm({ title: '', description: '', severity: 'medium', property_id: '', photo_ids: [], photo_urls: [] });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <PortalShell title="Damage Reports">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Damage & Incidents</h1>
          <p className="text-sm text-muted-foreground">Report damage or missing items. Add photos for context.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid={PORTAL.newReport}><Plus className="h-4 w-4 mr-2" />New report</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Report a problem</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Title *</Label><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid={PORTAL.reportTitle} /></div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Severity</Label>
                <select className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong" value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
              <div><Label>Property</Label>
                <select className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong" value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })}>
                  <option value="">(none)</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.nickname}</option>)}
                </select>
              </div>
              <div>
                <Label>Photos</Label>
                <div className="flex flex-wrap gap-2 items-center">
                  {form.photo_urls.map((u, i) => (
                    <div key={i} className="relative">
                      <img src={uploadUrl(u)} alt="attached" className="h-16 w-16 object-cover rounded-md border" />
                      <button type="button" onClick={() => setForm(f => ({ ...f, photo_ids: f.photo_ids.filter((_, x) => x !== i), photo_urls: f.photo_urls.filter((_, x) => x !== i) }))} className="absolute -top-1 -right-1 rounded-full bg-background border p-0.5"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files?.[0] && attach(e.target.files[0])} />
                    <div className="h-16 w-16 border-2 border-dashed rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground"><Camera className="h-5 w-5" /></div>
                  </label>
                </div>
              </div>
              <DialogFooter><Button type="submit" data-testid={PORTAL.reportSubmit}>Submit</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {!loaded ? <ListSkeleton /> : items.length === 0 ? <EmptyState icon={TriangleAlert} title="No reports" description="When you report an issue it will appear here." /> :
        <div className="space-y-3">
          {items.map(it => (
            <Card key={it.id}>
              <CardContent className="p-4 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{it.title}</div>
                  {it.description && <div className="text-sm text-muted-foreground mt-1">{it.description}</div>}
                  <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(it.created_at)}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="outline">{it.severity}</Badge>
                  <Badge variant="outline">{it.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }
    </PortalShell>
  );
}
